import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  completeServiceRequest,
  updateDoctorServiceRequest,
  updateRequestPatients,
  updateRequestInfo,
  getPatientEmrDocuments,
} from "api/serviceRequests";
import conversionBytes from "utils/conversionBytes";
import { extractFileNameFromUrl } from "@/constants/index.js";
import { queryClient } from "../queryClient";
import { REQUEST_STATUS } from "store/request/constants";
import { queryKeys } from "./keys";
import { invalidateServiceRequestCalendar } from "./serviceRequests";
import { CACHE } from "config/cachePolicy";

export function mapPatientEmrDocumentsResponse(resp) {
  if (!resp?.data?.length) {
    return { timestamp: resp?.timestamp, data: [] };
  }
  const arr = resp.data.map((i) => {
    let data = [];
    const docs = i.documents;
    const visitNote = docs?.emr_note || i.visit_note;
    if (visitNote?.file)
      data.push({
        ...visitNote,
        name: "Visit Note.pdf",
      });
    if (docs?.discharge_file)
      data.push({
        ...docs.discharge_file,
        name: "Discharge Summary.pdf",
      });
    if (docs?.receipt_file) data.push(docs.receipt_file);
    if (docs?.excuse_file) data.push(docs.excuse_file);
    if (docs?.covid_test_result_file)
      data.push({
        ...docs.covid_test_result_file,
        name: "Covid Test Result.pdf",
      });
    if (docs?.pcr_covid_test_result_file)
      data.push({
        ...docs.pcr_covid_test_result_file,
        name: "PCR Covid Test Result.pdf",
      });
    if (docs?.follow_up_files && Array.isArray(docs?.follow_up_files))
      data = [
        ...data,
        ...(docs.follow_up_files || []).map((file) => ({
          ...file,
          name: file.name?.replace(/_/g, " "),
        })),
      ];
    if (Array.isArray(i.attachments))
      data = [
        ...data,
        ...i.attachments.map((attachment) => ({
          ...attachment,
          name: attachment.name || extractFileNameFromUrl(attachment.file),
        })),
      ];
    return {
      ...i,
      documents: data.map((doc) => ({
        ...doc,
        download_link: doc.download_link || doc.file,
        size: conversionBytes(doc.size),
      })),
    };
  });
  return { timestamp: resp.timestamp, data: arr };
}

export function useCompleteServiceRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, files }) => completeServiceRequest(uuid, files),
    onSuccess: (_data, { uuid }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.serviceRequests.detail(uuid),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.serviceRequests.all,
      });
    },
  });
}

export function useUpdateDoctorServiceRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, body }) => updateDoctorServiceRequest(uuid, body),
    onSuccess: (_data, { uuid }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.serviceRequests.detail(uuid),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.serviceRequests.all,
      });
      invalidateServiceRequestCalendar(queryClient);
    },
  });
}

export function useUpdateRequestPatients() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, body }) => updateRequestPatients(uuid, body),
    onSuccess: (_data, { uuid }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.serviceRequests.detail(uuid),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.serviceRequests.all,
      });
    },
  });
}

export function useUpdateRequestInfo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, body }) => updateRequestInfo(uuid, body),
    onSuccess: (_data, { uuid }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.serviceRequests.detail(uuid),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.serviceRequests.all,
      });
    },
  });
}

export function usePatientEmrDocuments(id, options = {}) {
  return useQuery({
    queryKey: queryKeys.patients.emrDocuments(id),
    queryFn: () => getPatientEmrDocuments(id),
    enabled: !!id,
    staleTime: CACHE.LIVE,
    select: mapPatientEmrDocumentsResponse,
    ...options,
  });
}

export function usePatientRequestFiles(id, options = {}) {
  return usePatientEmrDocuments(id, options);
}

const syncServiceRequestCache = (resp) => {
  if (!resp?.data?.uuid) return resp;
  queryClient.setQueryData(queryKeys.serviceRequests.detail(resp.data.uuid), {
    timestamp: resp.timestamp,
    data: resp.data,
  });
  return resp;
};

export async function completeServiceRequestVisit(
  files,
  targetRequest,
  listRequests,
) {
  const resp = await completeServiceRequest(targetRequest.uuid, files);
  syncServiceRequestCache(resp);
  await invalidateServiceRequestCalendar(queryClient);
  const patientUuid =
    targetRequest?.patient_uuid || listRequests?.[0]?.patient_uuid;
  if (patientUuid && Array.isArray(listRequests)) {
    queryClient.setQueryData(
      queryKeys.emr.patientRequests(patientUuid),
      listRequests.map((i) =>
        i.service_request_uuid === targetRequest?.uuid
          ? { ...i, status: REQUEST_STATUS.COMPLETED }
          : i,
      ),
    );
  }
  return resp;
}

export async function updateServiceRequestData(uuid, body) {
  const resp = await updateDoctorServiceRequest(uuid, body);
  if (resp.data?.uuid) syncServiceRequestCache(resp);
  return resp;
}

export function patchServiceRequestPatientField(
  field,
  patientUuid,
  value,
  requestUuid,
) {
  const cached = queryClient.getQueryData(
    queryKeys.serviceRequests.detail(requestUuid),
  );
  if (!cached?.data?.patients) return;
  const idx = cached.data.patients.findIndex((i) => i.uuid === patientUuid);
  if (idx === -1) return;
  queryClient.setQueryData(queryKeys.serviceRequests.detail(requestUuid), {
    ...cached,
    data: {
      ...cached.data,
      patients: [
        ...cached.data.patients.slice(0, idx),
        { ...cached.data.patients[idx], [field]: value },
        ...cached.data.patients.slice(idx + 1),
      ],
    },
  });
}

export const updateRequestDetailsPatients = ({ uuid, body }) =>
  updateRequestPatients(uuid, body);

export const updateRequestDetailsCommonInfo = ({ uuid, body }) =>
  updateRequestInfo(uuid, body);
